import React, { useEffect, useMemo, useRef, useState } from "react";

import {
	MapContainer,
	Marker,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";

import L from "leaflet";

import "./ShopLocationPicker.css";

/*
 * Leaflet's default marker images can break with some
 * React/Webpack/Vite configurations.
 *
 * So we explicitly define the marker icon.
 */
const shopMarkerIcon = new L.Icon({
	iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",

	iconRetinaUrl:
		"https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",

	shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",

	iconSize: [25, 41],

	iconAnchor: [12, 41],

	popupAnchor: [1, -34],

	shadowSize: [41, 41],
});

/*
 * Default location.
 *
 * India is used only as the initial map position.
 * It is NOT saved as the shop location until
 * the shopkeeper actually selects a location.
 */
const DEFAULT_LOCATION = {
	latitude: 20.5937,
	longitude: 78.9629,
};

const MapClickHandler = ({ onSelect }) => {
	useMapEvents({
		click(event) {
			onSelect({
				latitude: event.latlng.lat,
				longitude: event.latlng.lng,
			});
		},
	});

	return null;
};

const MapCenterController = ({ location }) => {
	const map = useMap();

	useEffect(() => {
		if (location?.latitude === null || location?.longitude === null) {
			return;
		}

		map.flyTo([location.latitude, location.longitude], 16, {
			duration: 1,
		});
	}, [location, map]);

	return null;
};

const ShopLocationPicker = ({ location, onLocationChange }) => {
	const [locating, setLocating] = useState(false);

	const [locationError, setLocationError] = useState("");

	const mapRef = useRef(null);

	const selectedLocation =
		location?.latitude !== null && location?.longitude !== null
			? [location.latitude, location.longitude]
			: null;

	const mapCenter = selectedLocation || [
		DEFAULT_LOCATION.latitude,
		DEFAULT_LOCATION.longitude,
	];

	const handleMapLocation = (newLocation) => {
		setLocationError("");

		onLocationChange(newLocation);
	};

	const handleCurrentLocation = () => {
		if (!navigator.geolocation) {
			setLocationError(
				"Location services are not supported by this browser.",
			);

			return;
		}

		setLocating(true);

		setLocationError("");

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const newLocation = {
					latitude: position.coords.latitude,

					longitude: position.coords.longitude,
				};

				onLocationChange(newLocation);

				if (mapRef.current) {
					mapRef.current.flyTo(
						[newLocation.latitude, newLocation.longitude],
						17,
						{
							duration: 1,
						},
					);
				}

				setLocating(false);
			},

			(error) => {
				console.error("Current Location Error:", error);

				if (error.code === 1) {
					setLocationError(
						"Location permission was denied. Please allow location access or select the shop location manually on the map.",
					);
				} else if (error.code === 2) {
					setLocationError(
						"Your current location could not be determined. Please select it manually on the map.",
					);
				} else if (error.code === 3) {
					setLocationError(
						"Location request timed out. Please try again or select the location manually.",
					);
				} else {
					setLocationError("Unable to get your current location.");
				}

				setLocating(false);
			},

			{
				enableHighAccuracy: true,

				timeout: 15000,

				maximumAge: 0,
			},
		);
	};

	const markerEventHandlers = useMemo(
		() => ({
			dragend(event) {
				const marker = event.target;

				const position = marker.getLatLng();

				handleMapLocation({
					latitude: position.lat,

					longitude: position.lng,
				});
			},
		}),
		[],
	);

	return (
		<div className="shop-location-picker">
			{/* LOCATION HEADER */}

			<div className="shop-location-top">
				<button
					type="button"
					className="use-current-location-btn"
					onClick={handleCurrentLocation}
					disabled={locating}
				>
					<i className="fa-solid fa-location-crosshairs"></i>

					{locating ? "Locating..." : "Use Current Location"}
				</button>
			</div>

			{/* MAP */}

			<div className="shop-location-map">
				<MapContainer
					center={mapCenter}
					zoom={selectedLocation ? 16 : 5}
					scrollWheelZoom={true}
					className="register-map"
					ref={mapRef}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>

					<MapClickHandler onSelect={handleMapLocation} />

					<MapCenterController location={location} />

					{selectedLocation && (
						<Marker
							position={selectedLocation}
							icon={shopMarkerIcon}
							draggable={true}
							eventHandlers={markerEventHandlers}
						/>
					)}
				</MapContainer>

				<div className="map-instruction">
					<i className="fa-solid fa-hand-pointer"></i>

					<span>
						Click anywhere on the map to place the shop marker, then
						drag it for precise positioning.
					</span>
				</div>
			</div>

			{/* ERROR */}

			{locationError && (
				<div className="location-error">
					<i className="fa-solid fa-circle-exclamation"></i>
					<span>{locationError}</span>
				</div>
			)}

			{/* SELECTED COORDINATES */}

			<div
				className={`selected-location ${
					selectedLocation ? "selected" : ""
				}`}
			>
				<div className="selected-location-icon">
					<i className="fa-solid fa-location-dot"></i>
				</div>

				<div className="selected-location-content">
					<strong>
						{selectedLocation
							? "Shop Location Selected"
							: "Shop Location Not Selected"}
					</strong>

					{selectedLocation ? (
						<div className="coordinates">
							<span>
								Latitude:{" "}
								<strong>{location.latitude.toFixed(6)}</strong>
							</span>

							<span>
								Longitude:{" "}
								<strong>{location.longitude.toFixed(6)}</strong>
							</span>
						</div>
					) : (
						<p>
							Use current location or select a location from the
							map.
						</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default ShopLocationPicker;
